import './shim';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { AppWrapper as App } from './App';
import ErrorBoundary from './components/ErrorBoundary';
import './index.css';

const mountPublicApplication = () => {
 try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error("Critical: DOM element #root not found.");
  }

  const root = createRoot(rootElement);
  const serverShell = document.getElementById('public-ssr-shell');
  // The SSR shell and the client application have intentionally different
  // structures. Keep the root out of the paint tree only during the atomic
  // replacement so Lighthouse and real users never observe their intermediate
  // geometry as cumulative layout shift.
  rootElement.style.visibility = 'hidden';
  rootElement.setAttribute('aria-busy', 'true');
  let revealScheduled = false;
  let revealed = false;
  const revealClientApplication = () => {
    if (revealed || revealScheduled) return;
    if (document.documentElement.dataset.publicAppReady !== 'true') return;
    if (document.documentElement.dataset.publicMotionReady !== 'true') return;
    revealScheduled = true;
    // Both the stylesheet and the visibility-owning Motion features are ready.
    // Two frames give the browser one style/layout pass and then make the
    // replacement atomic without a fixed customer-visible pause.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      revealed = true;
      rootElement.style.removeProperty('visibility');
      rootElement.removeAttribute('aria-busy');
      rootElement.dataset.publicContentPhase = 'CLIENT_READY';
      document.querySelectorAll<HTMLElement>('[data-public-after-client]').forEach((element) => {
        element.style.visibility = 'visible';
      });
      if (serverShell) {
        serverShell.style.opacity = '0';
        serverShell.style.visibility = 'hidden';
        serverShell.style.pointerEvents = 'none';
        serverShell.setAttribute('aria-hidden', 'true');
        serverShell.inert = true;
        // The SSR shell is a first-paint/no-JavaScript fallback, not a second
        // live application. Remove it once the client is ready so its form,
        // IDs and controls cannot remain as duplicate DOM or accessibility
        // consumers behind the hydrated presentation.
        serverShell.remove();
      }
    }));
  };
  document.addEventListener('PUBLIC_APP_READY', revealClientApplication, { once: true });
  document.addEventListener('PUBLIC_MOTION_READY', revealClientApplication, { once: true });
  flushSync(() => {
    root.render(
      <React.StrictMode>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </React.StrictMode>
    );
  });
  revealClientApplication();
  window.setTimeout(revealClientApplication, 5_000);
 } catch (error) {
  console.error("[CRITICAL] Frontend mount failure:", error);
  document.body.innerHTML = `
    <div style="min-height:100vh; background:#0B1120; display:flex; align-items:center; justify-content:center; padding:24px; font-family:sans-serif;">
      <div style="max-width:600px; width:100%; background:#0F172A; border:1px solid rgba(30,41,59,0.5); border-radius:48px; padding:64px; text-align:center; box-shadow:0 25px 50px -12px rgba(0,0,0,0.5);">
        <div style="width:80px; height:80px; background:rgba(239,68,68,0.1); border-radius:24px; display:flex; align-items:center; justify-content:center; margin:0 auto 32px; border:1px solid rgba(239,68,68,0.2);">
          <svg style="width:40px; height:40px; color:#ef4444;" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 style="font-size:24px; font-weight:900; text-transform:uppercase; color:white; margin-bottom:16px; letter-spacing:-0.025em;">Erreur de chargement</h3>
        <p style="color:#94a3b8; margin-bottom:32px; font-weight:500; line-height:1.6;">L'application n'a pas pu démarrer. Cela peut être dû à un problème de connexion ou à une erreur critique du moteur.</p>
        <button onclick="window.location.reload()" style="width:100%; background:white; color:#0f172a; padding:16px; border-radius:16px; font-weight:900; text-transform:uppercase; letter-spacing:0.1em; border:none; cursor:pointer; transition:all 0.2s;">
          Réessayer
        </button>
        <p style="margin-top:32px; color:#475569; font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:0.3em;">Mobilbo Engine v2.0</p>
      </div>
    </div>`;
 }
};

// Keep the complete SSR shell visible until the nonblocking first-paint sheet
// is active. This avoids replacing stable styled HTML with an unstyled React
// tree on a slow network while preserving zero render-blocking stylesheets.
const publicAsyncStyle = document.querySelector<HTMLLinkElement>('link[data-public-async-style]');
if (!publicAsyncStyle || document.documentElement.dataset.publicStyleReady === 'true' || publicAsyncStyle.sheet) {
  mountPublicApplication();
} else {
  document.addEventListener('PUBLIC_STYLE_READY', mountPublicApplication, { once: true });
}
